import { useState } from 'react';

import {
  hasKeyProblem,
  integrationsApi,
  type Integration,
  type IntegrationType,
} from '@/api/queries/integrations';
import { useTenantStore } from '@/auth/tenantStore';
import { Icon, Tag, type IconName } from '@/components/ui';

import { IntegrationCardModal } from './IntegrationCardModal';

/**
 * Catalogue of integrations the UI knows how to render. Each entry is a
 * "kind" of integration; the user may have zero or more configured records
 * for it (one per Brevo account, etc.).
 */
interface IntegrationKind {
  type: IntegrationType;
  name: string;
  tagline: string;
  icon: IconName;
  /** Brand color for the card accent. */
  accent: string;
  /** Page where the user finds or creates their API key. */
  apiKeysUrl: string;
  /** Where to look for the key once on that page (provider-specific wording). */
  apiKeysHint: string;
  /** Placeholder illustrating this provider's key format. */
  apiKeyPlaceholder: string;
  /** REST base URL this provider's key authenticates against; also the default
   * value for the editable base-URL field when `supportsBaseUrl` is set. */
  apiEndpoint: string;
  /** Whether the connect form collects a sender (from_name/from_email/reply_to). */
  supportsSender?: boolean;
  /** Whether the connect form lets the user override the API base URL (per-tenant hosts). */
  supportsBaseUrl?: boolean;
}

const KINDS: IntegrationKind[] = [
  {
    type: 'brevo',
    name: 'Brevo',
    tagline: 'Mirror a segment to a Brevo list and run campaigns.',
    icon: 'mail',
    accent: '#0B996E',
    apiKeysUrl: 'https://app.brevo.com/settings/keys/api',
    apiKeysHint: 'SMTP & API → API keys',
    apiKeyPlaceholder: 'xkeysib-…',
    apiEndpoint: 'https://api.brevo.com/v3',
  },
  {
    type: 'ecomail',
    name: 'Ecomail',
    tagline: 'Mirror a segment to an Ecomail list and run campaigns.',
    icon: 'mail',
    accent: '#2F6BFF',
    apiKeysUrl: 'https://medicalinsider.ecomailapp.cz/account/developers',
    apiKeysHint: 'Account → For developers → Copy API Key',
    apiKeyPlaceholder: 'Your Ecomail API key',
    apiEndpoint: 'https://api2.ecomailapp.cz',
    supportsSender: true,
    supportsBaseUrl: true,
  },
  {
    type: 'mailchimp',
    name: 'Mailchimp',
    tagline: 'Mirror a segment to a Mailchimp audience and run campaigns.',
    icon: 'mail',
    accent: '#FFE01B',
    apiKeysUrl: 'https://admin.mailchimp.com/account/api/',
    apiKeysHint:
      "Profile → Extras → API keys — use a Marketing API key ending in -usNN; Transactional/Mandrill keys (md-…) don't work",
    apiKeyPlaceholder: '0123456789abcdef0123456789abcdef-us21',
    apiEndpoint: 'https://<dc>.api.mailchimp.com/3.0',
  },
];

export function IntegrationsPage() {
  // Integrations are tenant-scoped. Under the super-admin "all tenants" (*) view
  // there is no concrete tenant to attach an integration to, and the API rejects
  // the wildcard on this route with 403 — guide the user to pick a tenant instead.
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const needsConcreteTenant = currentTenantId === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Integrations</h1>
          <div className="page-subtitle">
            Connect Datahub to external mailing services. Configured integrations
            show up in the Connect to dialog on each segment so you can mirror an
            audience to a list and keep it in sync.
          </div>
        </div>
      </header>

      {needsConcreteTenant ? <SelectTenantNotice /> : <IntegrationsBody />}
    </div>
  );
}

function IntegrationsBody() {
  const { data: integrations = [], isPending } = integrationsApi.useList();
  const [openKind, setOpenKind] = useState<IntegrationKind | null>(null);

  return (
    <>
      {isPending ? (
        <div
          className="table-empty"
          style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
          }}
        >
          Loading integrations…
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {KINDS.map((kind) => {
            const accounts = integrations.filter((i) => i.type === kind.type);
            return (
              <IntegrationCard
                key={kind.type}
                kind={kind}
                accounts={accounts}
                onClick={() => setOpenKind(kind)}
              />
            );
          })}
        </div>
      )}

      {openKind && (
        <IntegrationCardModal
          kind={openKind}
          accounts={integrations.filter((i) => i.type === openKind.type)}
          open={!!openKind}
          onClose={() => setOpenKind(null)}
        />
      )}
    </>
  );
}

function SelectTenantNotice() {
  return (
    <div
      className="table-empty"
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        padding: '32px 20px',
        textAlign: 'center',
        color: 'var(--text-2)',
      }}
    >
      <Icon name="globe" size={24} style={{ color: 'var(--text-3)' }} />
      <div style={{ fontWeight: 600, marginTop: 8 }}>Select a tenant first</div>
      <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
        Integrations are configured per tenant. Pick a specific tenant from the
        switcher at the top of the sidebar to view and connect integrations.
      </div>
    </div>
  );
}

function IntegrationCard({
  kind,
  accounts,
  onClick,
}: {
  kind: IntegrationKind;
  accounts: Integration[];
  onClick: () => void;
}) {
  const active = accounts.some((a) => a.status === 'active');
  const keyProblem = accounts.some(hasKeyProblem);
  const dotColor = active ? '#10b981' : '#cbd5e1';
  const dotTitle = active ? 'Active' : 'Not connected';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 16,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-sm)',
            background: kind.accent,
            color: '#fff',
            display: 'inline-grid',
            placeItems: 'center',
          }}
        >
          <Icon name={kind.icon} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{kind.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{kind.tagline}</div>
        </div>
        {keyProblem && <Tag tone="red">Key problem</Tag>}
        <span
          title={dotTitle}
          aria-label={dotTitle}
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
          }}
        />
      </div>

      <div
        style={{
          fontSize: 12,
          color: 'var(--text-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {accounts.length === 0 ? (
          <>
            <Icon name="plus" size={11} />
            <span>Click to connect</span>
          </>
        ) : (
          <>
            <Icon name="check" size={11} style={{ color: '#10b981' }} />
            <span>
              {accounts.length} account{accounts.length === 1 ? '' : 's'} connected
            </span>
          </>
        )}
      </div>
    </button>
  );
}
